// Dispositivos de entrada y sensores I²C: joystick, encoder rotatorio, teclados de membrana,
// MPU-6050, relojes DS1307/DS3231, receptor IR con control remoto y báscula HX711.
// Las constantes de geometría (mm) las comparten la definición (pines) y el modelo 3D.
import { definePart, centeredRow, PITCH } from './registry.js';

/** Conector macho en ángulo recto en un borde: pines en columna (eje Z) que salen hacia ±X. */
function sideHeader(names, { x, y, labels = {}, kinds = {}, dir = -1, suffix = '' }) {
  return names.map((name, i) => ({
    name: name + suffix,
    label: labels[name] || name,
    kind: kinds[name] || 'signal',
    x,
    y,
    z: (i - (names.length - 1) / 2) * PITCH,
    dir: [dir, 0, 0],
  }));
}

// Joystick KY-023 -----------------------------------------------------------------------------------
export const JOYSTICK = {
  w: 34, d: 26, pcbY: 1.8, t: 1.6,
  stickX: 2.5,          // centro de la palanca
  pinX: -23,            // punta de los pines en ángulo recto
  pots: 10000,          // potenciómetros de 10 kΩ
  maxTilt: 25,          // grados de inclinación máxima
};
JOYSTICK.top = JOYSTICK.pcbY + JOYSTICK.t;

definePart({
  type: 'joystick',
  name: 'Joystick KY-023',
  category: 'entradas',
  description: 'Palanca de dos ejes con botón. VRx y VRy dan de 0 a 5 V según la posición (analogRead ≈ 512 en el centro); SW se une a GND al hundir la palanca.',
  keywords: 'joystick palanca ky-023 hw-504 analogico ejes thumbstick mando',
  props: [
    { key: 'x', label: 'Eje X (VRx)', type: 'slider', min: -1, max: 1, step: 0.01, default: 0, runtime: true, live: true },
    { key: 'y', label: 'Eje Y (VRy)', type: 'slider', min: -1, max: 1, step: 0.01, default: 0, runtime: true, live: true },
    { key: 'pressed', label: 'Hundir la palanca (SW)', type: 'button' },
  ],
  pins: sideHeader(['GND', '+5V', 'VRx', 'VRy', 'SW'], {
    x: JOYSTICK.pinX,
    y: JOYSTICK.top + 1.27,
    labels: { GND: 'GND', '+5V': '+5V', VRx: 'VRx (eje X, analógico)', VRy: 'VRy (eje Y, analógico)', SW: 'SW (botón a GND)' },
    kinds: { GND: 'gnd', '+5V': 'power', VRx: 'analog', VRy: 'analog', SW: 'digital' },
  }),
  footprint: { w: 40, d: 26.5, h: 31, x: -3 },
  interactive: 'stick',
  help: 'Arrastra la palanca en la vista 3D (vuelve sola al centro); un clic sin arrastrar la hunde. ' +
    'VRx y VRy son potenciómetros de 10 kΩ entre +5V y GND. SW no tiene resistencia de pull-up en el módulo: usa <code>pinMode(pin, INPUT_PULLUP)</code>.',
});

// Encoder rotatorio KY-040 --------------------------------------------------------------------------
export const ENCODER = {
  w: 27, d: 19, pcbY: 1.8, t: 1.6,
  knobX: 3.5,           // eje del encoder
  pinX: -19.5,
  detents: 20,          // pasos (detentes) por vuelta; cada uno es un ciclo de cuadratura
};
ENCODER.top = ENCODER.pcbY + ENCODER.t;

definePart({
  type: 'rotary-encoder',
  name: 'Encoder rotatorio KY-040',
  category: 'entradas',
  description: 'Perilla sin tope de 20 pasos por vuelta con botón. Cada paso da un ciclo de cuadratura en CLK y DT: en sentido horario CLK cambia antes que DT.',
  keywords: 'encoder rotatorio rotary ky-040 hw-040 perilla incremental cuadratura ec11',
  props: [
    { key: 'cw', label: 'Girar un paso ↻ (horario)', type: 'button' },
    { key: 'ccw', label: 'Girar un paso ↺ (antihorario)', type: 'button' },
    { key: 'press', label: 'Presionar la perilla (SW)', type: 'button' },
  ],
  pins: sideHeader(['CLK', 'DT', 'SW', '+', 'GND'], {
    x: ENCODER.pinX,
    y: ENCODER.top + 1.27,
    labels: { CLK: 'CLK (salida A)', DT: 'DT (salida B)', SW: 'SW (botón a GND)', '+': '+ (5 V)', GND: 'GND' },
    kinds: { CLK: 'digital', DT: 'digital', SW: 'digital', '+': 'power', GND: 'gnd' },
  }),
  footprint: { w: 33, d: 19.5, h: 21, x: -3 },
  interactive: 'encoder',
  help: 'En la vista 3D: rueda del mouse sobre la perilla para girarla y clic para presionarla. ' +
    'CLK y DT tienen resistencias de 10 kΩ a + en el módulo; SW no tiene pull-up (usa <code>INPUT_PULLUP</code>).',
});

// Teclados de membrana ------------------------------------------------------------------------------
export const KEYPAD_LAYOUTS = {
  keypad: {
    rows: 4, cols: 4, w: 69, d: 77, pitchX: 15.4, pitchZ: 15.8, key: 12.2, y0: -4.5,
    keys: ['1', '2', '3', 'A', '4', '5', '6', 'B', '7', '8', '9', 'C', '*', '0', '#', 'D'],
  },
  'keypad-3x4': {
    rows: 4, cols: 3, w: 51, d: 70, pitchX: 15.0, pitchZ: 14.6, key: 11.6, y0: -4.0,
    keys: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'],
  },
};
export const KEYPAD = { t: 0.9, cable: 76, conn: 8.5, connH: 2.6 };

export function keypadLayout(type) {
  return KEYPAD_LAYOUTS[type] || KEYPAD_LAYOUTS.keypad;
}

/** Nombres de los pines del conector: R1–R4 y luego C1–Cn. */
export function keypadPins(layout) {
  const names = [];
  for (let r = 1; r <= layout.rows; r++) names.push('R' + r);
  for (let c = 1; c <= layout.cols; c++) names.push('C' + c);
  return names;
}

/** z del extremo del conector (donde entran los cables). */
export function keypadConnectorZ(layout) {
  return layout.d / 2 + KEYPAD.cable + KEYPAD.conn;
}

for (const type of ['keypad', 'keypad-3x4']) {
  const L = KEYPAD_LAYOUTS[type];
  const names = keypadPins(L);
  const zEnd = keypadConnectorZ(L);
  definePart({
    type,
    name: type === 'keypad' ? 'Teclado de membrana 4×4' : 'Teclado de membrana 3×4',
    category: 'entradas',
    description: `${L.rows * L.cols} teclas en matriz de ${L.rows} filas por ${L.cols} columnas: cada tecla une una fila (R) con una columna (C). Se lee con la librería Keypad.`,
    keywords: 'teclado keypad membrana matriz matricial numerico 4x4 3x4 teclas',
    props: [{ key: 'keys', label: 'Teclas', type: 'keypad', keys: L.keys, columns: type === 'keypad' ? 4 : 3 }],
    pins: names.map((name, i) => ({
      name,
      label: `${i + 1} · ${name[0] === 'R' ? 'Fila' : 'Columna'} ${name.slice(1)}`,
      kind: 'digital',
      x: (i - (names.length - 1) / 2) * PITCH,
      y: KEYPAD.connH / 2,
      z: zEnd,
      dir: [0, 0, 1],
    })),
    footprint: { w: L.w + 1, d: zEnd + L.d / 2 + 1, h: 4, z: (zEnd - L.d / 2) / 2 },
    help: 'Haz clic en las teclas del modelo 3D o usa los botones de aquí. Cablea las filas y las columnas a pines digitales (no necesitan resistencias: Keypad usa los pull-ups internos).',
  });
}

// MPU-6050 (GY-521) ---------------------------------------------------------------------------------
export const MPU = { w: 21.2, d: 16.4, pcbY: 2.5, t: 1.6, pinZ: 6.6 };
MPU.top = MPU.pcbY + MPU.t;

definePart({
  type: 'mpu6050',
  name: 'Acelerómetro y giroscopio MPU-6050 (GY-521)',
  category: 'sensores',
  description: 'Acelerómetro y giroscopio de 3 ejes con termómetro, por I²C (dirección 0x68, o 0x69 con AD0 en alto). SDA a A4 y SCL a A5.',
  keywords: 'mpu6050 mpu-6050 gy-521 acelerometro giroscopio imu inclinacion i2c',
  insertable: true,
  props: [
    { key: 'ax', label: 'Aceleración X', type: 'slider', min: -4, max: 4, step: 0.01, default: 0, unit: 'g', runtime: true, live: true },
    { key: 'ay', label: 'Aceleración Y', type: 'slider', min: -4, max: 4, step: 0.01, default: 0, unit: 'g', runtime: true, live: true },
    { key: 'az', label: 'Aceleración Z', type: 'slider', min: -4, max: 4, step: 0.01, default: 1, unit: 'g', runtime: true, live: true },
    { key: 'gx', label: 'Giro X', type: 'slider', min: -500, max: 500, step: 1, default: 0, unit: '°/s', runtime: true, live: true },
    { key: 'gy', label: 'Giro Y', type: 'slider', min: -500, max: 500, step: 1, default: 0, unit: '°/s', runtime: true, live: true },
    { key: 'gz', label: 'Giro Z', type: 'slider', min: -500, max: 500, step: 1, default: 0, unit: '°/s', runtime: true, live: true },
    { key: 'temperature', label: 'Temperatura', type: 'slider', min: -40, max: 85, step: 0.5, default: 24, unit: '°C', runtime: true, live: true },
  ],
  pins: centeredRow(['VCC', 'GND', 'SCL', 'SDA', 'XDA', 'XCL', 'AD0', 'INT'], {
    z: MPU.pinZ,
    labels: {
      VCC: 'VCC (3.3–5 V)', GND: 'GND', SCL: 'SCL (reloj I²C)', SDA: 'SDA (datos I²C)',
      XDA: 'XDA (I²C auxiliar)', XCL: 'XCL (I²C auxiliar)', AD0: 'AD0 (dirección: GND = 0x68, VCC = 0x69)', INT: 'INT (interrupción)',
    },
    kinds: { VCC: 'power', GND: 'gnd', SCL: 'signal', SDA: 'signal', XDA: 'signal', XCL: 'signal', AD0: 'digital', INT: 'digital' },
  }),
  footprint: { w: 21.5, d: 16.8, h: 7 },
  help: 'Con la placa quieta y horizontal el acelerómetro mide 1 g en Z. Al encender el chip está dormido (PWR_MGMT_1 = 0x40): ' +
    'hay que despertarlo escribiendo 0 en el registro 0x6B (la librería Adafruit_MPU6050 lo hace en <code>begin()</code>). AD0 tiene una resistencia a GND en el módulo.',
});

// Relojes de tiempo real ----------------------------------------------------------------------------
export const DS1307_BOARD = { w: 27, d: 28, pcbY: 5.2, t: 1.6, p1X: -11.6, p2X: 11.6 };
DS1307_BOARD.top = DS1307_BOARD.pcbY + DS1307_BOARD.t;
DS1307_BOARD.pinY = DS1307_BOARD.top + 8.5;
// Conector P1 (7 agujeros): solo se sueldan los 5 pines que se usan.
export const DS1307_P1 = ['SQW', 'DS', 'SCL', 'SDA', 'VCC', 'GND', 'BAT'];

const batteryProp = {
  key: 'battery', label: 'Pila de respaldo (CR2032)', type: 'boolean', default: true,
};

definePart({
  type: 'ds1307',
  name: 'Reloj de tiempo real DS1307 (Tiny RTC)',
  category: 'sensores',
  description: 'Reloj con calendario y 56 bytes de RAM por I²C (0x68), con pila de respaldo. Trae una memoria EEPROM AT24C32 (0x50). Se usa con RTClib (RTC_DS1307).',
  keywords: 'rtc reloj ds1307 tiny rtc hora fecha calendario rtclib i2c',
  props: [batteryProp],
  pins: ['GND', 'VCC', 'SDA', 'SCL', 'SQW'].map((name) => ({
    name,
    label: { GND: 'GND', VCC: 'VCC (5 V)', SDA: 'SDA (datos I²C)', SCL: 'SCL (reloj I²C)', SQW: 'SQ (onda cuadrada, drenador abierto)' }[name],
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
    x: DS1307_BOARD.p1X,
    y: DS1307_BOARD.pinY,
    z: (DS1307_P1.indexOf(name) - 3) * PITCH,
  })),
  footprint: { w: 27.5, d: 28.5, h: 15 },
  help: 'Con la pila, el reloj arranca con la fecha y hora reales del momento en que inicia la simulación y sigue contando aunque se quite VCC. ' +
    'Sin pila arranca detenido (bit CH) en 01/01/2000. El DS1307 necesita 5 V: con 3.3 V no responde.',
});

export const DS3231_BOARD = { w: 38, d: 22, pcbY: 5.2, t: 1.6, pinX: 25.6 };
DS3231_BOARD.top = DS3231_BOARD.pcbY + DS3231_BOARD.t;

definePart({
  type: 'ds3231',
  name: 'Reloj de tiempo real DS3231 (ZS-042)',
  category: 'sensores',
  description: 'Reloj de alta precisión con termómetro, alarmas y onda cuadrada, por I²C (0x68), con pila de respaldo y EEPROM AT24C32 (0x57). Se usa con RTClib (RTC_DS3231).',
  keywords: 'rtc reloj ds3231 zs-042 hora fecha calendario alarma temperatura rtclib i2c',
  props: [
    batteryProp,
    { key: 'temperature', label: 'Temperatura', type: 'slider', min: -40, max: 85, step: 0.25, default: 24, unit: '°C', runtime: true, live: true },
  ],
  pins: [
    ...sideHeader(['32K', 'SQW', 'SCL', 'SDA', 'VCC', 'GND'], {
      x: -DS3231_BOARD.pinX,
      y: DS3231_BOARD.top + 1.27,
      labels: { '32K': '32K (32.768 kHz)', SQW: 'SQW (onda cuadrada o alarma)', SCL: 'SCL (reloj I²C)', SDA: 'SDA (datos I²C)', VCC: 'VCC (3.3–5 V)', GND: 'GND' },
      kinds: { VCC: 'power', GND: 'gnd' },
    }),
    ...sideHeader(['SCL', 'SDA', 'VCC', 'GND'], {
      x: DS3231_BOARD.pinX,
      y: DS3231_BOARD.top + 1.27,
      dir: 1,
      suffix: '.2',
      labels: { SCL: 'SCL (paso)', SDA: 'SDA (paso)', VCC: 'VCC (paso)', GND: 'GND (paso)' },
      kinds: { VCC: 'power', GND: 'gnd' },
    }),
  ],
  buses: [['SCL', 'SCL.2'], ['SDA', 'SDA.2'], ['VCC', 'VCC.2'], ['GND', 'GND.2']],
  footprint: { w: 51.5, d: 22.5, h: 9 },
  help: 'Con la pila, el reloj arranca con la fecha y hora reales y <code>lostPower()</code> da falso; sin pila arranca en 01/01/2000 con la bandera OSF encendida. ' +
    'La temperatura se convierte cada 64 s (o al pedirlo con el bit CONV). SQW y 32K tienen resistencias de 4.7 kΩ a VCC en el módulo.',
});

// Receptor IR VS1838B con control remoto -------------------------------------------------------------
// Control «Car MP3» de 21 teclas (protocolo NEC, dirección 0x00).
export const IR_REMOTE_KEYS = [
  { label: 'CH−', code: 0x45 }, { label: 'CH', code: 0x46 }, { label: 'CH+', code: 0x47 },
  { label: '⏮', code: 0x44 }, { label: '⏭', code: 0x40 }, { label: '⏯', code: 0x43 },
  { label: '−', code: 0x07 }, { label: '+', code: 0x15 }, { label: 'EQ', code: 0x09 },
  { label: '0', code: 0x16 }, { label: '100+', code: 0x19 }, { label: '200+', code: 0x0D },
  { label: '1', code: 0x0C }, { label: '2', code: 0x18 }, { label: '3', code: 0x5E },
  { label: '4', code: 0x08 }, { label: '5', code: 0x1C }, { label: '6', code: 0x5A },
  { label: '7', code: 0x42 }, { label: '8', code: 0x52 }, { label: '9', code: 0x4A },
];
export const IR_ADDRESS = 0x00;
// Control remoto delante del receptor, con el LED apuntando hacia él.
export const IR_REMOTE = { w: 40, l: 86, h: 6.4, z0: 16, y: 4 };

definePart({
  type: 'ir-receiver',
  name: 'Receptor infrarrojo VS1838B con control',
  category: 'entradas',
  description: 'Receptor de 38 kHz (salida activa en bajo) y un control remoto «Car MP3» de 21 teclas que manda códigos NEC. Se lee con la librería IRremote.',
  keywords: 'infrarrojo ir receptor vs1838b tsop control remoto nec irremote car mp3',
  insertable: true,
  props: [{ key: 'remote', label: 'Control remoto', type: 'keypad', keys: IR_REMOTE_KEYS.map((k) => k.label), columns: 3 }],
  pins: centeredRow(['OUT', 'GND', 'VCC'], {
    labels: { OUT: 'OUT (salida, activa en bajo)', GND: 'GND', VCC: 'VCC (2.7–5.5 V)' },
    kinds: { OUT: 'digital', GND: 'gnd', VCC: 'power' },
  }),
  footprint: { w: IR_REMOTE.w + 1, d: IR_REMOTE.z0 + IR_REMOTE.l + 5, h: 16, z: (IR_REMOTE.z0 + IR_REMOTE.l - 4) / 2 },
  help: 'Haz clic en las teclas del control (o usa los botones de aquí): mientras la mantienes presionada se repite el código cada 108 ms. ' +
    'Dirección NEC 0x00; por ejemplo CH− = 0x45, ⏯ = 0x43, 0 = 0x16, 1 = 0x0C.',
});

// Báscula: módulo HX711 con celda de carga de barra --------------------------------------------------
export const HX711_BOARD = { w: 34, d: 21, pcbY: 1.6, t: 1.6, pinX: -14.9 };
HX711_BOARD.top = HX711_BOARD.pcbY + HX711_BOARD.t;
HX711_BOARD.pinY = HX711_BOARD.top + 8.5;
export const LOAD_CELL = { x0: 30, len: 80, side: 12.7, y0: 6, platformX: 96, platformR: 22 };
// Celda de 1 mV/V excitada desde AVDD con ganancia 128: el fondo de escala (±0.5·AVDD/128) son
// ±2^23 cuentas, así que cada gramo vale 2·0.001·128·2^23 / capacidad = 2147.48 cuentas por kg de capacidad.
export const HX711_COUNTS_FULL = 2 * 0.001 * 128 * 2 ** 23;
export const HX711_CELLS = {
  1000: { label: '1 kg' },
  5000: { label: '5 kg' },
};
export function hx711Factor(capacity) {
  return HX711_COUNTS_FULL / (Number(capacity) || 1000);
}

definePart({
  type: 'hx711',
  name: 'Báscula: HX711 con celda de carga',
  category: 'sensores',
  description: 'Convertidor de 24 bits HX711 con una celda de carga de barra (galga extensiométrica). Da 10 lecturas por segundo por DT/SCK; se usa con la librería HX711.',
  keywords: 'hx711 celda de carga bascula peso balanza galga load cell gramos',
  props: [
    { key: 'capacity', label: 'Celda', type: 'select', default: 1000, options: Object.entries(HX711_CELLS).map(([v, c]) => ({ value: Number(v), label: c.label })) },
    { key: 'weight', label: 'Peso sobre la celda', type: 'slider', min: 0, max: 5000, step: 1, default: 0, unit: 'g', runtime: true, live: true },
  ],
  pins: ['GND', 'DT', 'SCK', 'VCC'].map((name, i) => ({
    name,
    label: { GND: 'GND', DT: 'DT (datos, DOUT)', SCK: 'SCK (reloj, PD_SCK)', VCC: 'VCC (2.6–5.5 V)' }[name],
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'digital',
    x: HX711_BOARD.pinX,
    y: HX711_BOARD.pinY,
    z: (i - 1.5) * PITCH,
  })),
  footprint: { w: 140, d: 46, h: 30, x: 53 },
  help: 'Lectura cruda = desplazamiento + peso × factor. Con ganancia 128 el factor es ≈ 2147.5 cuentas/g para la celda de 1 kg y ≈ 429.5 para la de 5 kg: ' +
    'usa ese valor en <code>set_scale()</code> y <code>tare()</code> para el cero. Mantener SCK en alto más de 60 µs apaga el HX711.',
});
